import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

// MUI imports
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";

export default function ApiKeys({ token }) {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("viewer");
  const [loading, setLoading] = useState(false);

  const fetchKeys = async () => {
    try {
      const resp = await axios.get(
        "https://api.harishacrypto.xyz/service18/api/auth/apikeys",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKeys(resp.data.data);
    } catch (e) {
      Swal.fire("Error", e.response?.data?.error || e.message, "error");
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const showMessage = (title, text, icon = "success") => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonColor: "#4f46e5",
    });
  };

  const createKey = async () => {
    if (!name) return showMessage("Error", "Please enter a key name", "error");
    setLoading(true);
    try {
      const resp = await axios.post(
        "https://api.harishacrypto.xyz/service18/api/auth/apikeys",
        { name, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Success", `API Key created: ${resp.data.api_key}`);
      setName("");
      fetchKeys();
    } catch (e) {
      showMessage("Error", e.response?.data?.error || e.message, "error");
    }
    setLoading(false);
  };

  const revokeKey = async (id) => {
    setLoading(true);
    try {
      await axios.post(
        `https://api.harishacrypto.xyz/service18/api/auth/apikeys/${id}/revoke`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Revoked", "API Key has been revoked", "success");
      fetchKeys();
    } catch (e) {
      showMessage("Error", e.response?.data?.error || e.message, "error");
    }
    setLoading(false);
  };

  return (
    <Box p={4}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          🔑 API Keys Management
        </Typography>
      </motion.div>

      {/* Create Key Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 4 }}>
          <CardContent sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Key Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
            <Button
              variant="contained"
              color="primary"
              onClick={createKey}
              disabled={loading}
              sx={{
                minWidth: 150,
                background: "linear-gradient(90deg, #4f46e5, #9333ea)",
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Create Key"}
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* API Keys Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Role</b></TableCell>
                  <TableCell><b>Created</b></TableCell>
                  <TableCell><b>Revoked</b></TableCell>
                  <TableCell><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>{k.name}</TableCell>
                    <TableCell>{k.role}</TableCell>
                    <TableCell>
                      {new Date(k.created_at * 1000).toLocaleString()}
                    </TableCell>
                    <TableCell>{k.revoked ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      {!k.revoked && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => revokeKey(k.id)}
                          disabled={loading}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {keys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No API keys found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
}
